import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import InputField from '../components/InputField';
import { getToken } from '../utils/authToken';
import Navbar from '../components/Navbar';
import ConfirmationModal from '../components/Alerts/ConfirmationModal';
import AlertModal from '../components/Alerts/AlertModal';
import PrivacyPolicyPopup from '../components/PrivacyPolicy';
import { TailSpin } from 'react-loader-spinner';

const Register: React.FC = () => {
  const router = useRouter();
  const register_url = process.env.NEXT_PUBLIC_BACK_END_URL + '/api/v1/auth/register';
  const [formData, setFormData] = useState({ name: '', surname: '', email: '', password: '', confirmPassword: '', privacy: false, marketing: true });
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null); 
  const [modalAction, setModalAction] = useState<(() => void) | null>(null);
  const [modalButton, setModalButton] = useState<string | null>(null);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  

  useEffect(() => {
    if (getToken()) {
      router.push('/dashboard');
    }
  }, [router]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {setFormData({ ...formData, name: e.target.value })};
  const handleSurnameChange = (e: React.ChangeEvent<HTMLInputElement>) => {setFormData({ ...formData, surname: e.target.value })};
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {setFormData({ ...formData, email: e.target.value })};
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {setFormData({ ...formData, password: e.target.value })};
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {setFormData({ ...formData, confirmPassword: e.target.value })};
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>, skipMarketingCheck: boolean = false) => {
    if (e) e.preventDefault();

    // Define password pattern for validation
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]).{8,50}$/;

    // Marketing consent check
    if (!formData.marketing && !skipMarketingCheck) {
        setShowMarketingModal(true)
        return;
    }

    // Password validation check
    if (!passwordPattern.test(formData.password)) {
        setModalContent(
            <>
                <h1 className="font-bold mb-4 text-red-700 text-2xl">Errore</h1>
                <p>
                    La password non rispetta i requisiti.<br />
                    Deve contenere almeno 8 caratteri ma non più di 50, una lettera maiuscola, un simbolo e un numero.
                </p>
            </>
        );
        setIsModalVisible(true);
        return;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
        setModalContent(
            <>
                <h1 className="font-bold mb-4 text-red-700 text-2xl">Errore</h1>
                <p>Le password non coincidono. Riprova.</p>
            </>
        );
        setIsModalVisible(true);
        return;
    }

    // Show loading state
    setLoading(true);

    // Attempt registration
    try {
        const response = await fetch(register_url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                firstName: formData.name,
                lastName: formData.surname,
                email: formData.email,
                password: formData.password,
                marketing_consent: formData.marketing,
                privacy_consent: formData.privacy,
            }),
        });

        const data = await response.json();

        if (response.ok) {
            // Success modal
            setModalContent(
                <>
                    <h1 className="font-bold mb-4 text-secondary text-2xl">Benvenuto!</h1>
                    <p>
                        Controlla la tua email. <br />
                        Potrebbe anche essere nelle spam. <br />
                        Se così non fosse, riprova più tardi.
                    </p>
                </>
            );
            setIsModalVisible(true);
        } else if (response.status === 409) {
            // Already registered modal
            setModalContent(
                <>
                    <h1 className="font-bold mb-4 text-2xl">Ci siamo già conosciuti</h1>
                    <p>Sei già iscritto/a al nostro servizio con queste credenziali.</p>
                </>
            );
            setModalButton("Vai al login")
            setModalAction(() => () => router.push('/login'))
            setIsModalVisible(true);
        } else {
            // Generic error modal
            setModalContent(
                <>
                    <h1 className="font-bold mb-4 text-2xl">Acciderbolina!</h1>
                    <p>Qualcosa è andato storto! Riprova più tardi oppure controlla di aver scritto bene la mail.</p>
                </>
            );
            setIsModalVisible(true);
        }
    } catch (err) {
        // Network error modal
        console.error('Registration error:', err);
        setModalContent(
            <>
                <h1 className="font-bold mb-4 text-2xl">Errore</h1>
                <p>C&apos;è stato un errore durante la registrazione. Riprova più tardi.</p>
            </>
        );
        setIsModalVisible(true);
    } finally {
        setLoading(false); // Reset loading state after request finishes
    }
  };

  return (
    <>
      <Navbar />
      <div className="bg-[#f4f4fe] min-h-screen flex items-center justify-center absolute moving-tile-bg z-[-5]"></div>
      <div className="min-h-screen flex items-center justify-center z-0">
        <div className="bg-white p-8 rounded-3xl shadow-md w-full max-w-md">
          <h1 className="text-primary text-3xl font-bold mb-6 text-center">Registrati qui</h1>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="flex space-x-4">
                <InputField
                  label="Nome"
                  type="text"
                  id="name"
                  value={formData.name}
                  placeholder="es. Luca"
                  required
                  onChange={handleNameChange}
                />
                <InputField
                  label="Cognome"
                  type="text"
                  id="surname"
                  value={formData.surname}
                  placeholder="es. Zhang"
                  required
                  onChange={handleSurnameChange}
                />
              </div>
            </div>
            <InputField
              label="Email"
              type="email"
              id="email"
              value={formData.email}
              placeholder="es. zhang_lu@libero.it"
              pattern="[A-Za-z0-9._+\-']+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$"
              required
              onChange={handleEmailChange}
              autoComplete="username"
            />
            <InputField
              label="Password"
              type="password"
              id="password"
              value={formData.password}
              placeholder="La tua password"
              required
              title="La password deve contenere almeno 8 caratteri, una lettera maiuscola, un simbolo e un numero."
              onChange={handlePasswordChange}
              autoComplete="new-password"
            />
            <InputField
              label="Conferma Password"
              type="password"
              id="confirmPassword"
              value={formData.confirmPassword}
              placeholder="Conferma la tua password"
              required
              title="La password deve contenere almeno 8 caratteri, una lettera maiuscola, un simbolo e un numero."
              onChange={handleConfirmPasswordChange}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
            />
            <div className="!mt-5">
              <label htmlFor="privacy" className="inline-flex items-start">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacy"
                  className="rounded border-gray-300 text-primary focus:border-primary mt-[4px] focus:ring-0"
                  checked={formData.privacy}
                  onChange={handleCheckboxChange}
                  required
                />
                <span className="ml-2 text-xs text-dark-blue">
                  <span className='font-bold'>[Necessario]</span> Acconsento al trattamento dei miei dati personali in quanto stabilito dalla {' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyPopup(true)}
                    className="text-primary font-bold underline"
                  >
                    politica sulla privacy di Geraldone
                  </button>{' '}
                  in conformità alla G.D.P.R. e al D.Lgs.196/2003.
                </span>
              </label>
            </div>
            <div>
              <label htmlFor="marketing" className="inline-flex items-start">
                <input
                  type="checkbox"
                  id="marketing"
                  name="marketing"
                  className="rounded border-gray-300 text-primary focus:border-primary mt-[4px] focus:ring-0"
                  checked={formData.marketing}
                  onChange={handleCheckboxChange}
                />
                <span className="ml-2 text-xs text-dark-blue">
                  Acconsento a ricevere utili reminder via mail riguardo le prossime scadenze fiscali e newsletter a scopo di marketing.
                </span>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading} // Disable button if loading
              className={`font-bold text-dark-blue w-full py-2 rounded-full !mt-8 text-xl transition-colors ${
                loading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-secondary hover:bg-primary hover:text-white"
              }`}
            >
              {loading ? (
                <div className="flex justify-center">
                  <TailSpin height="25" width="25" color="#333" />
                </div>
              ) : (
                "Registrati"
              )}
            </button>
          </form>
          <p className="mt-4 text-center">
            Hai già un account?{' '}
            <Link href="/login" className="text-primary font-bold">Accedi</Link>
          </p>
        </div>

        {/* Modals for marketing consent, registration status, errors, and privacy popup */}
        {showMarketingModal && (
            <ConfirmationModal
                invertButtons={true}
                message={
                  <>
                  <h1 className='font-bold mb-4 text-2xl'>ATTENZIONE !!!</h1>
                  Acconsentendo di ricevere le nostre newsletter, Geraldone ti notificherà in anticipo delle scadenze fiscali evitando che tu incorra in sanzioni.<br />
                  Sei sicuro di procedere senza acconsentire?
                  </>}
                onConfirm={() => {handleSubmit(undefined, true)}}
                onCancel={() => {
                  setFormData({ ...formData, marketing: true });
                  setShowMarketingModal(false);
                }}
            />
        )}
        {isModalVisible && (
            <AlertModal message={modalContent} onClose={modalAction || (() => setIsModalVisible(false))} buttonCustom={modalButton || "Chiudi"} />
        )}
        {showPrivacyPopup && <PrivacyPolicyPopup isOpen={showPrivacyPopup} closePopup={() => setShowPrivacyPopup(false)} />}
      </div>
    </>
  );
};

export default Register;
